/**
 * Filtre « One Euro » (Casiez et al., CHI 2012) : lisse fortement la gigue
 * quand le visage est immobile, suit sans retard quand il bouge vite.
 */
import type { HandPlacement } from './chinAnchor';

export interface OneEuroOptions {
  /** Fréquence de coupure au repos (Hz) : plus bas = plus lisse. */
  readonly minCutoff?: number;
  /** Réactivité à la vitesse : plus haut = moins de retard en mouvement. */
  readonly beta?: number;
  readonly dCutoff?: number;
}

export interface OneEuro {
  /** `t` en ms. */
  filter(value: number, t: number): number;
  reset(): void;
}

const alpha = (cutoff: number, dt: number): number => 1 / (1 + 1 / (2 * Math.PI * cutoff) / dt);

export function createOneEuro({
  minCutoff = 1,
  beta = 0.01,
  dCutoff = 1,
}: OneEuroOptions = {}): OneEuro {
  let last: { t: number; x: number; dx: number } | null = null;
  return {
    filter(value, t) {
      if (!last) {
        last = { t, x: value, dx: 0 };
        return value;
      }
      const dt = (t - last.t) / 1000;
      if (!(dt > 0)) return last.x;
      const rawDx = (value - last.x) / dt;
      const dx = last.dx + alpha(dCutoff, dt) * (rawDx - last.dx);
      const cutoff = minCutoff + beta * Math.abs(dx);
      const x = last.x + alpha(cutoff, dt) * (value - last.x);
      last = { t, x, dx };
      return x;
    },
    reset() {
      last = null;
    },
  };
}

const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));

export interface PlacementSmoother {
  push(p: HandPlacement, t: number): HandPlacement;
  reset(): void;
}

/** Lissage de la main : position, taille, inclinaison (sans saut de ±π). */
export function createPlacementSmoother(): PlacementSmoother {
  const x = createOneEuro({ minCutoff: 1.2, beta: 0.02 });
  const y = createOneEuro({ minCutoff: 1.2, beta: 0.02 });
  const width = createOneEuro({ minCutoff: 0.8, beta: 0.005 });
  const angle = createOneEuro({ minCutoff: 1, beta: 0.4 });
  let rawAngle: number | null = null;
  return {
    push(p, t) {
      // Angle « déroulé » : on suit le chemin le plus court d'une mesure à l'autre.
      rawAngle = rawAngle === null ? p.angle : rawAngle + wrap(p.angle - rawAngle);
      const w = width.filter(p.width, t);
      return {
        x: x.filter(p.x, t),
        y: y.filter(p.y, t),
        angle: wrap(angle.filter(rawAngle, t)),
        width: w,
        height: w * (p.height / p.width),
        clamped: p.clamped,
      };
    },
    reset() {
      for (const f of [x, y, width, angle]) f.reset();
      rawAngle = null;
    },
  };
}
